import type { MechanicDefinition } from "./types";

export const magicSystem: MechanicDefinition = {
  id: "magic_system",
  disabled: true,
  name: "Powers & Magic",
  description: "Unified power system — mana pool, spell slots, freeform, or corruption-based",
  icon: "Sparkles",
  category: "systems",
  aiHints: {
    purpose: "Provides a magic/power system with mana, spell slots, freeform casting, or corruption mechanics.",
    auditPasses: 1,
    whenToUse: "Any world with supernatural abilities — fantasy magic, sci-fi psionics, superhero powers.",
    synergies: ["combat", "skill_checks", "character_classes", "conditions"],
    conflicts: [],
    prerequisites: [],
    tags: ["magic", "spells", "mana", "powers", "supernatural"],
    complexity: 3,
    exampleConfig: { style: "mana_pool", max_mana: 50, school_based: true },
    stateVariables: ["player_mana", "player_max_mana", "known_spells"],
  },
  configSchema: [
    {
      key: "type",
      label: "Resource System",
      type: "select",
      options: [
        { value: "mana", label: "Mana Pool (spend points)" },
        { value: "slots", label: "Spell Slots (limited uses per rest)" },
        { value: "freeform", label: "Freeform (narrative cost)" },
        { value: "corruption", label: "Corruption (power at a price)" },
      ],
      default: "mana",
    },
    {
      key: "power_level",
      label: "Power Level",
      type: "select",
      options: [
        { value: "low", label: "Low (subtle, rare)" },
        { value: "medium", label: "Medium (common, balanced)" },
        { value: "high", label: "High (world-shaping)" },
      ],
      default: "medium",
    },
    {
      key: "max_spell_level",
      label: "Max Spell/Power Level",
      type: "number",
      default: 9,
      description: "Highest tier of power available (only for slots mode)",
    },
    {
      key: "cantrips",
      label: "Allow Free Minor Powers",
      type: "boolean",
      default: true,
      description: "Basic powers that don't cost resources",
    },
    {
      key: "recovery",
      label: "Resource Recovery",
      type: "select",
      options: [
        { value: "rest", label: "Rest (sleep/meditation)" },
        { value: "time", label: "Time (regenerates slowly)" },
        { value: "items", label: "Items (potions, artifacts)" },
        { value: "ritual", label: "Ritual (special action)" },
      ],
      default: "rest",
    },
  ],
  systemPromptSection: (config) => {
    const type = config.type || "mana";
    const power = config.power_level || "medium";
    const maxLevel = config.max_spell_level || 9;
    const cantrips = config.cantrips !== false;
    const recovery = config.recovery || "rest";

    const typeDesc: Record<string, string> = {
      mana: `Mana pool system. Track "mana" (current/max) in state. Spells cost mana. ${recovery === "time" ? "Mana regenerates 1 point per game-hour." : `Mana recovers on ${recovery}.`}`,
      slots: `Spell slot system (up to level ${maxLevel}). Track "spell_slots" as {level: {used, max}} in state. Higher-level slots for stronger spells. Upcasting allowed. Slots recover on ${recovery}.`,
      freeform: `Narrative cost system. Magic has consequences: exhaustion, material components, time, pain. The bigger the power, the bigger the price. No numeric tracking — enforce through storytelling.`,
      corruption: `Corruption system. Track "corruption" (0-100) in state. Minor powers are safe (+1-5). Major powers risk mutation, madness (+10-25). At 50+ visible signs appear. At 100 the character is lost. Corruption reduces by 1 per day of rest.`,
    };

    return `## POWERS & MAGIC (Rules)
- System: ${type}. Power level: ${power}.
- ${typeDesc[type]}
- ${cantrips ? "Minor powers (cantrips/level 0) are free and unlimited." : "All powers cost resources."}
- Power level context: ${power === "low" ? "Powers are rare and subtle. Most problems solved mundanely." : power === "high" ? "Powers are common and world-shaping." : "Powers are available but balanced against non-magical approaches."}
- Use cast_spell tool for any power use. Always enforce costs — no free magic.
- Track "known_spells" as array of {name, level, school, effect} in state.
- Describe power effects vividly. Each school/type has a distinct feel.

### NARRATIVE ACCESS REQUIREMENTS
- Magic is NOT a menu. Players must narratively learn and access their powers.
- New spells/abilities require in-world discovery: finding a mentor, studying a spellbook, completing a trial, or divine revelation.
- If world context specifies HOW magic is learned/accessed, enforce that path strictly.
- If no world context, invent a fitting source (academy, ancient tome, spirit guide, innate awakening) matching the setting.
- When a player asks to "learn a spell" or "check available spells," narrate the in-world process.
- Spell components, focuses, and rituals should be narrated, not abstracted away.`;
  },
  widgets: [
    {
      id: "magic_status",
      type: "status_bar_item",
      label: "Magic",
      icon: "Sparkles",
      component: "MagicWidget",
      dataBindings: {
        mana: "variables.mana",
        maxMana: "variables.max_mana",
        corruption: "variables.corruption",
        spellSlots: "variables.spell_slots",
        knownSpells: "variables.known_spells",
      },
      visibility: "when_active",
      priority: 6,
    },
    {
      id: "magic_tab",
      type: "sidebar_tab",
      label: "Magic",
      icon: "Sparkles",
      component: "MagicWidget",
      dataBindings: {
        mana: "variables.mana",
        maxMana: "variables.max_mana",
        corruption: "variables.corruption",
        spellSlots: "variables.spell_slots",
        knownSpells: "variables.known_spells",
      },
      visibility: "when_data",
      priority: 45,
    },
  ],
  initializeState: (config) => {
    const type = config.type || "mana";
    const defaults: Record<string, any> = {
      _magic_type: type,
      known_spells: [],
    };
    if (type === "mana") {
      defaults.mana = 20;
      defaults.max_mana = 20;
    } else if (type === "corruption") {
      defaults.corruption = 0;
    } else if (type === "slots") {
      defaults.spell_slots = { "1": { used: 0, max: 2 } };
    }
    return defaults;
  },
  tools: [
    {
      type: "function",
      function: {
        name: "cast_spell",
        description: "Cast a spell or use a power. Handles resource cost deduction and effect resolution.",
        parameters: {
          type: "object",
          properties: {
            spell_name: { type: "string", description: "Name of the spell or power" },
            spell_level: { type: "number", description: "Power level (0 for cantrip/minor)" },
            cost: { type: "number", description: "Resource cost (mana points, slot level, corruption gained)" },
            target: { type: "string", description: "Target of the spell" },
            effect: { type: "string", description: "What the spell does narratively" },
          },
          required: ["spell_name", "spell_level", "effect"],
        },
      },
    },
  ],
};
